/**
 * Wraps an authenticated route. On mount probes /api/portal/me and, when
 * unauthenticated, redirects to /login with a `?redirect=` hint so the
 * post-login navigation lands back here.
 *
 * The probe also serves as the portal_csrf seed for any subsequent unsafe
 * POSTs the child makes.
 */
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { seedCsrfAndProbeSession } from '../lib/auth';

type AuthState = 'checking' | 'allowed' | 'denied' | 'error';

interface RequireAuthProps {
  children: React.ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const [state, setState] = useState<AuthState>('checking');
  const location = useLocation();

  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      const outcome = await seedCsrfAndProbeSession(ctrl.signal);
      if (outcome === 'authenticated') setState('allowed');
      else if (outcome === 'unauthenticated') setState('denied');
      else setState('error');
    })();
    return () => ctrl.abort();
  }, [location.pathname]);

  if (state === 'checking') {
    return (
      <section aria-busy="true">
        <p>Checking your sign-in status…</p>
      </section>
    );
  }
  if (state === 'denied') {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }
  if (state === 'error') {
    return (
      <section role="alert">
        <p>We couldn&apos;t verify your sign-in status. Please wait a moment and try again.</p>
      </section>
    );
  }
  return <>{children}</>;
}
